import { useEffect, useMemo, useState } from 'react';
import {
  checkAnswerAgainstExpected,
  formatChoiceIds,
  formatChoiceRevealText,
  getChoiceOptions,
  normalizeAnswerText,
  parseChoiceIds,
} from '../utils/workAttemptHelpers';

/**
 * Выбор вариантов ответа (single_choice / multiple_choice) + проверка / показать ответ.
 * Блок под условием задачи (не header).
 */
export default function ChoiceAnswerPanel({
  answerType = 'single_choice',
  answerSpec,
  expectedAnswer,
  taskKey = '',
  value,
  onChange,
  onCheck,
  onReveal,
  saving = false,
  className = '',
}) {
  const controlled = value !== undefined;
  const [internalAnswer, setInternalAnswer] = useState('');
  const [hint, setHint] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);

  const options = useMemo(() => getChoiceOptions(answerSpec), [answerSpec]);
  const isMultiple = answerType === 'multiple_choice';
  const studentAnswer = controlled ? String(value ?? '') : internalAnswer;
  const selected = useMemo(
    () => new Set(parseChoiceIds(studentAnswer).map((id) => normalizeAnswerText(id))),
    [studentAnswer],
  );
  const hasExpected = expectedAnswer != null && String(expectedAnswer).trim() !== '';
  const hasOptions = options.length > 0;

  useEffect(() => {
    if (!controlled) setInternalAnswer('');
    setHint(null);
    setShowAnswer(false);
  }, [taskKey, expectedAnswer, answerType, controlled]);

  if (!hasOptions) return null;

  function setAnswer(next) {
    if (controlled) {
      if (typeof onChange === 'function') onChange(next);
    } else {
      setInternalAnswer(next);
    }
    setHint(null);
  }

  function selectSingle(id) {
    setAnswer(String(id));
  }

  function toggleMultiple(id) {
    const key = normalizeAnswerText(id);
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    // порядок выбора сохраняем относительно порядка options
    const ordered = options.map((o) => o.id).filter((oid) => next.has(normalizeAnswerText(oid)));
    setAnswer(formatChoiceIds(ordered, { sort: false }));
  }

  function handleCheck() {
    if (!hasExpected) {
      setHint('wrong');
      return;
    }
    const ok = checkAnswerAgainstExpected(studentAnswer, expectedAnswer, answerType);
    setHint(ok ? 'ok' : 'wrong');
    if (typeof onCheck === 'function') {
      void onCheck({ ok, studentAnswer });
    }
  }

  function handleRevealToggle() {
    if (showAnswer) {
      setShowAnswer(false);
      return;
    }
    setShowAnswer(true);
    if (typeof onReveal === 'function') {
      void onReveal({ studentAnswer });
    }
  }

  const revealText = formatChoiceRevealText(expectedAnswer, options);

  const hintNode =
    hint === 'ok' ? (
      <span className="work-answer-hint work-answer-hint-ok">Верно</span>
    ) : hint === 'wrong' ? (
      <span className="work-answer-hint work-answer-hint-wrong">Неверно</span>
    ) : null;

  return (
    <div className={`choice-answer-panel ${className}`.trim()}>
      <div className="choice-answer-panel-label">
        {isMultiple ? 'Выберите все верные варианты' : 'Выберите один вариант'}
      </div>
      <ul className="choice-answer-options" role={isMultiple ? 'group' : 'radiogroup'}>
        {options.map((opt) => {
          const checked = selected.has(normalizeAnswerText(opt.id));
          const inputId = `choice-${taskKey}-${opt.id}`;
          return (
            <li key={opt.id} className={`choice-answer-option${checked ? ' is-selected' : ''}`}>
              <label htmlFor={inputId} className="choice-answer-option-label">
                <input
                  id={inputId}
                  type={isMultiple ? 'checkbox' : 'radio'}
                  name={isMultiple ? undefined : `choice-${taskKey}`}
                  checked={checked}
                  disabled={saving}
                  onChange={() => {
                    if (isMultiple) toggleMultiple(opt.id);
                    else selectSingle(opt.id);
                  }}
                />
                <span className="choice-answer-option-id">{opt.id}.</span>
                <span className="choice-answer-option-text">{opt.text}</span>
              </label>
            </li>
          );
        })}
      </ul>
      <div className="work-student-answer-actions">
        <button
          type="button"
          className="btn btn-outline btn-sm"
          disabled={saving || !hasExpected}
          onClick={handleCheck}
        >
          {saving ? '…' : 'Проверить'}
        </button>
        {hasExpected ? (
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={saving && !showAnswer}
            onClick={handleRevealToggle}
          >
            {showAnswer ? 'Скрыть ответ' : saving ? '…' : 'Показать ответ'}
          </button>
        ) : null}
        {hintNode}
      </div>
      {showAnswer && revealText ? (
        <div className="answer-block task-answer-check-reveal">
          <pre className="choice-answer-reveal-text">{revealText}</pre>
        </div>
      ) : null}
    </div>
  );
}
